import { useState, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import DevisActionsService from '../services/devisActionsService';

/**
 * Hook React pour gérer les actions sur les devis
 * Fournit une interface simple pour utiliser le service d'actions
 */
const useDevisActions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionEnCours, setActionEnCours] = useState(null);

  // Instance du service d'actions
  const actionsService = useMemo(() => new DevisActionsService(db), []);

  /**
   * Exécuter une action sur un devis
   */
  const executerAction = useCallback(async (actionId, devis, options = {}) => {
    setLoading(true);
    setActionEnCours(actionId);
    setError(null);

    try {
      const resultat = await actionsService.executerAction(actionId, devis, options);
      setLoading(false);
      setActionEnCours(null);
      return resultat;
    } catch (err) {
      console.error(`Erreur lors de l'action ${actionId}:`, err);
      setError(err.message);
      setLoading(false);
      setActionEnCours(null);
      throw err;
    }
  }, [actionsService]);

  /**
   * Actions rapides prédéfinies
   */
  const actions = useMemo(() => ({
    // Actions de base
    voirDetails: (devis) => executerAction('voir', devis),
    modifier: (devis, modifications) => executerAction('modifier', devis, modifications),
    envoyer: (devis, options) => executerAction('envoyer', devis, options),
    accepter: (devis, options) => executerAction('accepter', devis, options),
    refuser: (devis, options) => executerAction('refuser', devis, options),
    negocier: (devis, options) => executerAction('negocier', devis, options),
    
    // Actions de gestion
    creerFacture: (devis, options) => executerAction('creer_facture', devis, options),
    dupliquer: (devis, options) => executerAction('dupliquer', devis, options),
    telechargerPDF: (devis, options) => executerAction('telecharger', devis, options),
    imprimer: (devis, options) => executerAction('imprimer', devis, options),
    archiver: (devis, options) => executerAction('archiver', devis, options),
    supprimer: (devis, options) => executerAction('supprimer', devis, options),
    
    // Actions de suivi
    relancerClient: (devis, options) => executerAction('relancer', devis, options),
    reouvrir: (devis, options) => executerAction('reouvrir', devis, options),
    renouveler: (devis, options) => executerAction('renouveler', devis, options),
    partager: (devis, utilisateurs, options) => executerAction('partager', devis, { utilisateurs, ...options }),
    
    // Actions personnalisées
    executerActionPersonnalisee: executerAction
  }), [executerAction]);

  /**
   * Vérifier si une action est disponible selon le statut du devis
   */
  const estActionDisponible = useCallback((actionId, devis, userRole = 'admin') => {
    const actionsParStatut = {
      [devis.statut]: {
        'voir': true,
        'dupliquer': true,
        'telecharger': true,
        'imprimer': true,
        'modifier': devis.statut === 'brouillon',
        'envoyer': devis.statut === 'brouillon',
        'supprimer': devis.statut === 'brouillon' && userRole === 'admin',
        'relancer': devis.statut === 'envoye',
        'accepter': devis.statut === 'envoye' && userRole === 'admin',
        'refuser': devis.statut === 'envoye' && userRole === 'admin',
        'negocier': devis.statut === 'envoye',
        'creer_facture': devis.statut === 'accepte' && userRole === 'admin',
        'archiver': devis.statut === 'accepte',
        'partager': devis.statut === 'accepte',
        'reouvrir': devis.statut === 'refuse' && userRole === 'admin',
        'analyser': devis.statut === 'refuse',
        'renouveler': devis.statut === 'expire' && userRole === 'admin'
      }
    };

    return actionsParStatut[devis.statut]?.[actionId] || false;
  }, []);

  /**
   * Obtenir les actions disponibles pour un devis
   */
  const getActionsDisponibles = useCallback((devis, userRole = 'admin') => {
    const toutesLesActions = [
      { id: 'voir', label: 'Voir détails', description: 'Afficher les détails complets' },
      { id: 'modifier', label: 'Modifier', description: 'Modifier le devis en cours' },
      { id: 'envoyer', label: 'Envoyer', description: 'Envoyer au client par email' },
      { id: 'accepter', label: 'Accepter', description: 'Accepter et convertir en client' },
      { id: 'refuser', label: 'Refuser', description: 'Refuser avec raison' },
      { id: 'negocier', label: 'Négocier', description: 'Ouvrir une négociation avec le client' },
      { id: 'creer_facture', label: 'Créer facture', description: 'Créer une facture' },
      { id: 'dupliquer', label: 'Dupliquer', description: 'Créer une copie' },
      { id: 'telecharger', label: 'Télécharger PDF', description: 'Télécharger en PDF' },
      { id: 'imprimer', label: 'Imprimer', description: 'Imprimer le devis' },
      { id: 'archiver', label: 'Archiver', description: 'Archiver le devis' },
      { id: 'supprimer', label: 'Supprimer', description: 'Supprimer définitivement' },
      { id: 'relancer', label: 'Relancer client', description: 'Envoyer un rappel' },
      { id: 'reouvrir', label: 'Réouvrir', description: 'Réouvrir pour modification' },
      { id: 'renouveler', label: 'Renouveler', description: 'Renouveler le devis expiré' },
      { id: 'partager', label: 'Partager', description: 'Partager avec d\'autres utilisateurs' }
    ];

    return toutesLesActions.filter(action => 
      estActionDisponible(action.id, devis, userRole)
    );
  }, [estActionDisponible]);

  /**
   * Réinitialiser l'état d'erreur
   */
  const reinitialiserErreur = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Obtenir des informations sur l'action en cours
   */
  const getInfoActionEnCours = useCallback(() => {
    if (!actionEnCours) return null;

    const libellesActions = {
      'voir': 'Affichage des détails',
      'modifier': 'Modification en cours',
      'envoyer': 'Envoi du devis',
      'accepter': 'Acceptation du devis',
      'refuser': 'Refus du devis',
      'negocier': 'Ouverture de négociation',
      'creer_facture': 'Création de la facture',
      'dupliquer': 'Duplication du devis',
      'telecharger': 'Génération du PDF',
      'imprimer': 'Préparation de l\'impression',
      'archiver': 'Archivage en cours',
      'supprimer': 'Suppression du devis',
      'relancer': 'Envoi de la relance',
      'reouvrir': 'Réouverture du devis',
      'renouveler': 'Renouvellement en cours',
      'partager': 'Partage du devis'
    };

    return {
      id: actionEnCours,
      libelle: libellesActions[actionEnCours] || 'Action en cours',
      progression: 'En cours...'
    };
  }, [actionEnCours]);

  return {
    // État
    loading,
    error,
    actionEnCours: getInfoActionEnCours(),
    
    // Actions
    actions,
    
    // Méthodes utilitaires
    estActionDisponible,
    getActionsDisponibles,
    reinitialiserErreur,
    
    // Méthode générique
    executerAction
  };
};

export default useDevisActions;
